import { useSelector } from 'react-redux'
import { profileSelector } from 'selectors'
import { Helmet } from 'react-helmet'
import Page from 'rui/widgets/Page'
import DriveSidebar from 'components/layout/DriveSidebar'
import Panel, { PanelTitle, PanelHeading, PanelSubheading, PanelSpacer } from 'components/layout/Panel'
import Thin from 'components/layout/Thin'
import LicenseInfo from './LicenseInfo'
import Vehicles, { NewVehicleForm } from './Vehicles'
import { getLicenseExpirationDate } from 'util/vehicle'
import { Container, Form, Spacer, Text } from 'roadie-ui'
import { states } from 'types/states'
import Step from 'components/misc/Step'
import { formatRegistrationStep } from 'util/registrationFlow'
import { useDrivingInfoRegistrationFormViewModel } from './hooks/useDrivingInfoRegistrationFormViewModel'
import { useDrivingInfoPageViewModel } from './hooks/useDrivingInfoPageViewModel'
import { PendingPlateLookupModal } from './PendingPlateLookupModal'
import { FlaggedInsuranceSubmissionModal } from './VehicleInfo/InsuranceSubmission'
import { useLocalization, LocalizedText } from 'localization'

const DrivingInfoRegistrationForm: React.FC = () => {
  const { t } = useLocalization()
  const { driversLicense, handleData, validations } = useDrivingInfoRegistrationFormViewModel()
  const { profile } = useSelector(profileSelector)
  const registrationStep = formatRegistrationStep('DRIVING_INFO', profile)

  return (
    <Form.Basic id="driving-info-form" onSubmit={handleData} validations={validations as any}>
      <Spacer $height="0.8rem" />
      <Container.Base $textAlign="left">
        <PanelTitle paddingLeft="0">
          <LocalizedText translationKey="dweb_account_driving_info_drivers_license" />
        </PanelTitle>
      </Container.Base>
      <Form.Input
        className="fs-block"
        label={
          driversLicense && driversLicense.number_last_four
            ? `******${driversLicense.number_last_four}`
            : t('dweb_account_driving_info_drivers_license_number')
        }
        name="number"
        key={driversLicense.hashed_number}
      />
      <Container.Grid $gridGap="1.5rem" $gridTemplateColumns="1fr 1fr">
        <Form.Select
          label={t('dweb_account_driving_info_vehicle_info_state')}
          name="state"
          defaultValue={driversLicense.state}
        >
          {states.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </Form.Select>
        <Form.Input
          type="date"
          label={t('dweb_account_driving_info_exp_date')}
          defaultValue={getLicenseExpirationDate(driversLicense)}
          name="expiration_date"
        />
      </Container.Grid>
      <Spacer $height="2.4rem" />
      <Container.Base $textAlign="left">
        <PanelTitle paddingLeft="0">
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info" />
        </PanelTitle>
      </Container.Base>
      <Text.Body2 $textAlign="left">
        <LocalizedText translationKey="dweb_account_driving_info_if_have_more_than_one_vehicle_we_recommend" />
      </Text.Body2>
      <Spacer $height="1.6rem" />
      <NewVehicleForm registration={true} />
      <Container.GridItem $maxWidth="40rem" $width="100%" $justifySelf="center">
        <Form.Submit>
          <LocalizedText translationKey="dweb_common_next" />
        </Form.Submit>
        {registrationStep && <Step>{registrationStep}</Step>}
      </Container.GridItem>
    </Form.Basic>
  )
}

const DrivingInfoPage: React.FC<{ registered: boolean }> = (props) => {
  const { t } = useLocalization()
  const { isWebView, pendingVehicle, modalOptions, setModalOptions } = useDrivingInfoPageViewModel()

  return props.registered ? (
    // Registered Driving Info Page
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_common_driving_info')}</title>
      </Helmet>
      {!isWebView && (
        <PanelTitle>
          <LocalizedText translationKey="dweb_common_driving_info" />
        </PanelTitle>
      )}
      <Panel left noPadding>
        <LicenseInfo />
      </Panel>
      <PanelSpacer />
      <PanelTitle>
        <LocalizedText translationKey="dweb_account_driving_info_vehicles" />
      </PanelTitle>
      <Vehicles />
      {pendingVehicle && (
        <>
          <PendingPlateLookupModal
            vehicle={pendingVehicle}
            modalOptions={modalOptions}
            setModalOptions={setModalOptions}
          />
          <FlaggedInsuranceSubmissionModal
            vehicle_id={pendingVehicle.details.id}
            modalOptions={modalOptions}
            setModalOptions={setModalOptions}
            fromFailureModal={false}
            openModalOnTryAgain={false}
          />
        </>
      )}
    </Page>
  ) : (
    // Registration Flow Driving Info Page
    <Page>
      <Helmet>
        <title>{t('dweb_account_driving_info_title')}</title>
      </Helmet>
      <Thin>
        <Panel>
          <PanelHeading>
            <LocalizedText translationKey="dweb_account_driving_info_title" />
          </PanelHeading>
          <PanelSubheading>
            <LocalizedText translationKey="dweb_account_driving_info_provide_license_vehicle_information" />
          </PanelSubheading>
          <DrivingInfoRegistrationForm />
        </Panel>
      </Thin>
    </Page>
  )
}

export default DrivingInfoPage
